// const API_URL = 'http://localhost:3000';
// const HEADERS = { Authorization: 'secret-token' };

// export async function getUsers() {
//     const res = await fetch(`${API_URL}/users`);
//     return res.json();
// }

// export async function getUser(id) {
//     const res = await fetch(`${API_URL}/users/${id}`);
//     return res.json();
// }

// export async function createUser(user) {
//     const res = await fetch(`${API_URL}/users`, {
//         method: 'POST',
//         headers: {'Content-Type': 'application/json'},
//         body: JSON.stringify(user),
//     });
//     return res.json();
// }

// function getAuthHeaders() {
//     const token = localStorage.getItem('access_token');

//     return {
//         'Content-Type': 'application/json',
//         ...(token ? { Authorization: `Bearer ${token}` } : {}),
//     };
// }

export const API_URL =
  process.env.REACT_APP_API_URL ||
  'http://localhost:3000';

/**
 * Media is stored as a path, not an absolute URL, so a database moved between
 * hosts keeps working. The host belongs to the moment of rendering, and this is
 * where it is put back on.
 */
export function mediaUrl(path) {
  if (!path) {
    return path;
  }

  return /^(https?:|data:|blob:)/.test(path)
    ? path
    : `${API_URL}${path.startsWith('/') ? path : `/${path}`}`;
}

/*
 * Fired on any 401 whose message identifies it as a dead session (expired,
 * revoked, timed out from inactivity - see server/src/auth/session.service.ts)
 * rather than a request-specific auth check (wrong login/current password).
 * AuthProvider listens for this to clear its state and bounce to /login -
 * without it, an API call made after the server-side inactivity timeout
 * would just surface as a confusing per-page error instead of a logout.
 */
export const SESSION_EXPIRED_EVENT = 'disi:session-expired';

const SESSION_EXPIRED_MESSAGES = new Set([
  'Authentication required',
  'Session is invalid or expired',
  'User is inactive or does not exist',
]);

export class ApiError extends Error {
  constructor(
    message,
    {
      status = null,
      data = null,
      cause = null,
    } = {},
  ) {
    super(message);

    this.name = 'ApiError';
    this.status = status;
    this.data = data;

    if (cause) {
      this.cause = cause;
    }
  }
}

function isFormData(body) {
  return (
    typeof FormData !== 'undefined' &&
    body instanceof FormData
  );
}

function prepareBody(body, headers) {
  if (
    body === undefined ||
    body === null
  ) {
    return undefined;
  }

  if (
    isFormData(body) ||
    typeof body === 'string' ||
    body instanceof Blob ||
    body instanceof URLSearchParams
  ) {
    return body;
  }

  if (!headers.has('Content-Type')) {
    headers.set(
      'Content-Type',
      'application/json',
    );
  }

  return JSON.stringify(body);
}

async function parseResponse(response) {
  if (
    response.status === 204 ||
    response.status === 205
  ) {
    return null;
  }

  const contentType =
    response.headers.get('content-type') || '';

  if (
    contentType.includes(
      'application/json',
    )
  ) {
    try {
      return await response.json();
    } catch {
      return null;
    }
  }

  const text = await response.text();

  return text || null;
}

function getErrorMessage(
  response,
  data,
) {
  if (
    data &&
    typeof data === 'object' &&
    !Array.isArray(data)
  ) {
    if (Array.isArray(data.message)) {
      return data.message.join(', ');
    }

    if (
      typeof data.message === 'string'
    ) {
      return data.message;
    }

    if (
      typeof data.error === 'string'
    ) {
      return data.error;
    }
  }

  if (typeof data === 'string' && data) {
    return data;
  }

  return `Request failed with status ${response.status}.`;
}

export async function apiRequest(
  path,
  options = {},
) {
  const {
    method = 'GET',
    headers: customHeaders,
    body,
    signal,
    ...restOptions
  } = options;

  const headers = new Headers(
    customHeaders || {},
  );

  const preparedBody =
    prepareBody(body, headers);

  let response;

  try {
    response = await fetch(
      `${API_URL}${path}`,
      {
        ...restOptions,
        method,
        headers,
        body: preparedBody,
        signal,
        credentials: 'include',
      },
    );
  } catch (error) {
    if (error.name === 'AbortError') {
      throw error;
    }

    throw new ApiError(
      'Could not connect to the server.',
      {
        cause: error,
      },
    );
  }

  const data =
    await parseResponse(response);

  if (!response.ok) {
    if (
      response.status === 401 &&
      SESSION_EXPIRED_MESSAGES.has(data?.message)
    ) {
      window.dispatchEvent(
        new CustomEvent(SESSION_EXPIRED_EVENT, {
          detail: { message: data.message },
        }),
      );
    }

    throw new ApiError(
      getErrorMessage(response, data),
      {
        status: response.status,
        data,
      },
    );
  }

  return data;
}

/*
 * Authentication
 */

export function login({
  login,
  password,
  rememberMe = false,
  otp,
  rememberDevice = false,
}) {
  return apiRequest('/auth/login', {
    method: 'POST',
    body: {
      login,
      password,
      rememberMe,
      ...(otp ? { otp, rememberDevice } : {}),
    },
  });
}

export function getCurrentUser() {
  return apiRequest('/auth/me');
}

/** Whether to offer single sign-on, and the label of its button. */
export function getOidcConfig() {
  return apiRequest('/auth/oidc/config');
}

/**
 * Where the browser goes to sign in through the identity provider: a
 * top-level navigation, not a fetch - the provider's pages take over the tab
 * and redirect back to `returnTo` with the session cookie set.
 */
export function oidcLoginUrl(returnTo = '/') {
  return `${API_URL}/auth/oidc/login?returnTo=${encodeURIComponent(returnTo)}`;
}

export function logout() {
  return apiRequest('/auth/logout', {
    method: 'POST',
  });
}

export function updateProfile(profile) {
  return apiRequest('/auth/me', {
    method: 'PATCH',
    body: profile,
  });
}

export function uploadAvatar(file) {
  const formData = new FormData();
  formData.append('file', file);

  return apiRequest('/uploads/avatar', {
    method: 'POST',
    body: formData,
  });
}

export function changePassword({
  currentPassword,
  newPassword,
}) {
  return apiRequest('/auth/me/password', {
    method: 'PATCH',
    body: {
      currentPassword,
      newPassword,
    },
  });
}

export function setupTwoFactor() {
  return apiRequest('/auth/2fa/setup', {
    method: 'POST',
  });
}

export function enableTwoFactor(code) {
  return apiRequest('/auth/2fa/enable', {
    method: 'POST',
    body: { code },
  });
}

// "Allen Geräten das Vertrauen entziehen": every browser asks for the code again.
export function revokeTrustedDevices() {
  return apiRequest('/auth/2fa/trusted-devices/revoke', {
    method: 'POST',
  });
}

export function disableTwoFactor(password) {
  return apiRequest('/auth/2fa/disable', {
    method: 'POST',
    body: { password },
  });
}

export function getPreferences() {
  return apiRequest('/auth/me/preferences');
}

export function updatePreferences(preferences) {
  return apiRequest('/auth/me/preferences', {
    method: 'PATCH',
    body: preferences,
  });
}

/*
 * AI assistant
 */

export function analyzeArticleWithAI({ title, text }) {
  return apiRequest('/ai-assistant/analyze-article', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      title,
      text,
    }),
  });
}

/*
 * Articles
 */

export function getArticle(id) {
  return apiRequest(`/articles/${encodeURIComponent(id)}`);
}

export function getArticlesByTag(tagName) {
  return apiRequest(`/articles/tag/${encodeURIComponent(tagName)}`);
}

export function getAllTags({ signal } = {}) {
  return apiRequest('/articles/tags', { signal });
}

export function getMyArticles() {
  return apiRequest('/articles/my');
}

// Articles (not news) matching `query`, best match first; `signal` lets a
// newer keystroke cancel a request still in flight.
export function searchArticles(query, { signal, limit } = {}) {
  const params = new URLSearchParams({ q: query });
  if (limit) params.set('limit', String(limit));
  return apiRequest(`/articles/search?${params}`, { signal });
}

// Articles to suggest for a free-text question the live search found nothing
// for: `{ keywords, articles }`, searched by the question's subject words.
/**
 * Runs an action the assistant offered (POST /assistant/actions): `{ source,
 * key, params }` as the answer gave them. Answers `{ url }`, the page to open.
 */
export function runAssistantAction({ source, key, params }) {
  return apiRequest('/assistant/actions', {
    method: 'POST',
    body: { source, key, params: params ?? {} },
  });
}

/**
 * Asks the knowledge assistant (POST /assistant/ask). `earlier` is the chat so
 * far as `[{ question, answer }]`, oldest first. Answers `{ answer, articles,
 * action, degraded }`.
 */
export function askAssistant(question, earlier = []) {
  return apiRequest('/assistant/ask', {
    method: 'POST',
    body: { question, ...(earlier.length ? { earlier } : {}) },
  });
}

// isNews: true for news, false for articles proper, omitted for both.
export function getArticles(isNews) {
  return apiRequest(
    isNews === undefined ? '/articles' : `/articles?is_news=${isNews}`,
  );
}

export function updateArticle(id, article) {
  return apiRequest(
    `/articles/${encodeURIComponent(id)}`,
    {
      method: 'PATCH',
      body: article,
    },
  );
}


/*
 * News
 */

// `limit`: only the newest N - the home carousel asks for as many as fit.
export function getNewsList(limit) {
  const query = limit ? `&limit=${encodeURIComponent(limit)}` : '';

  return apiRequest(`/articles?is_news=true${query}`);
}

export function getNewsById(id) {
  return apiRequest(
    `/articles/${encodeURIComponent(id)}`,
  );
}

/*
* Articles creation
*/
export function createArticle(article) {
  return apiRequest('/articles', {
    method: 'POST',
    body: article,
  });
}

export async function uploadPdf(file) {
  const formData = new FormData();

  formData.append(
    'file',
    file,
  );

  const result = await apiRequest(
    '/articles/upload',
    {
      method: 'POST',
      body: formData,
    },
  );

  return result;
}

/**
 * `target` says what the file is for, and so which right the server asks:
 * 'article' (articles.manage), 'news' (news.manage) or 'interface'
 * (interface.manage, e.g. a sidebar icon).
 */
/**
 * The vector drawings the PDF import produced. They are SVG, which the ordinary
 * upload refuses on purpose - this route takes them only after the server has
 * read the file and found nothing in it but geometry.
 */
export function uploadImportedSvg(file, target) {
  const formData = new FormData();

  formData.append(
    'file',
    file,
  );

  return apiRequest(
    `/uploads/import-svg?target=${encodeURIComponent(target)}`,
    {
      method: 'POST',
      body: formData,
    },
  );
}

export function uploadMedia(file, target = 'interface') {
  const formData = new FormData();
  formData.append('file', file);

  return apiRequest(
    `/uploads?target=${encodeURIComponent(target)}`,
    {
      method: 'POST',
      body: formData,
    },
  );
}

/*
 * Admin
 */

export function getAdminUsers() {
  return apiRequest('/admin/users');
}

/**
 * Where the caller's users.manage holds: `{ platform, departmentIds }`. A
 * department administrator may hand out only groups of those departments.
 */
export function getAdminUserScope() {
  return apiRequest('/admin/users/scope');
}

export function createAdminUser(user) {
  return apiRequest('/admin/users', {
    method: 'POST',
    body: user,
  });
}

// A colleague who lost their authenticator: 2FA off, sessions ended.
export function resetUserTwoFactor(userId) {
  return apiRequest(`/admin/users/${userId}/two-factor/reset`, {
    method: 'PATCH',
  });
}

// Who has to use 2FA: { twoFactorPolicy: 'off' | 'admins' | 'all' }.
export function getSecuritySettings() {
  return apiRequest('/admin/security');
}

export function updateSecuritySettings(twoFactorPolicy) {
  return apiRequest('/admin/security', {
    method: 'PATCH',
    body: { twoFactorPolicy },
  });
}

export function resetUserPassword(userId, password) {
  return apiRequest(
    `/admin/users/${encodeURIComponent(userId)}/password`,
    {
      method: 'PATCH',
      body: { password },
    },
  );
}

export function activateUser(userId) {
  return apiRequest(
    `/admin/users/${encodeURIComponent(userId)}/activate`,
    {
      method: 'PATCH',
    },
  );
}

export function deactivateUser(userId) {
  return apiRequest(
    `/admin/users/${encodeURIComponent(userId)}/deactivate`,
    {
      method: 'PATCH',
    },
  );
}

// Groups assignable inside a department - unlike the global list this one
// includes Administrators, so a department administrator can be appointed.
export function getAdminDepartments() {
  return apiRequest('/admin/departments');
}

export function createAdminDepartment(department) {
  return apiRequest('/admin/departments', {
    method: 'POST',
    body: department,
  });
}

/**
 * Replaces the user's groups. Each group carries its own department, so the
 * complete set of group ids is the whole membership; what is not in it is gone.
 */
export function setAdminUserGroups(userId, groupIds) {
  return apiRequest(
    `/admin/users/${encodeURIComponent(userId)}/groups`,
    { method: 'PATCH', body: { groupIds } },
  );
}

/** Every group with its department and the rights it grants. */
export function getAdminGroups() {
  return apiRequest('/admin/groups');
}

export function createAdminGroup(group) {
  return apiRequest('/admin/groups', {
    method: 'POST',
    body: group,
  });
}

/** A signed address of a click simulation on the simulation host. */
export function getSimulationLaunch(packageId) {
  return apiRequest(`/packages/${encodeURIComponent(packageId)}/launch`);
}

/** Deletes a group with its memberships; refused while an ACL names it. */
export function deleteAdminGroup(groupId) {
  return apiRequest(`/admin/groups/${encodeURIComponent(groupId)}`, {
    method: 'DELETE',
  });
}

/** Deletes an empty department. */
export function deleteAdminDepartment(departmentId) {
  return apiRequest(
    `/admin/departments/${encodeURIComponent(departmentId)}`,
    { method: 'DELETE' },
  );
}

/** Replaces the rights of one group. */
export function setAdminGroupRights(groupId, rightIds) {
  return apiRequest(
    `/admin/groups/${encodeURIComponent(groupId)}/rights`,
    { method: 'PATCH', body: { rightIds } },
  );
}

/** Every right the code checks, with what granting it does. */
export function getAdminRights() {
  return apiRequest('/admin/rights');
}

/** Provider groups and the DiSI groups they grant (single sign-on). */
export function getAdminIdpMappings() {
  return apiRequest('/admin/idp-mappings');
}

export function createAdminIdpMapping({ idpGroup, groupId }) {
  return apiRequest('/admin/idp-mappings', {
    method: 'POST',
    body: { idpGroup, groupId },
  });
}

export function deleteAdminIdpMapping(mappingId) {
  return apiRequest(
    `/admin/idp-mappings/${encodeURIComponent(mappingId)}`,
    { method: 'DELETE' },
  );
}

/*
 * Media pool (media.manage)
 */

// Filters left empty are not sent: the server's defaults apply.
export function getMediaPool(params = {}) {
  const query = new URLSearchParams(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null && value !== '',
    ),
  );

  return apiRequest(`/admin/media?${query}`);
}

export function getMediaPoolItem(mediaId) {
  return apiRequest(`/admin/media/${encodeURIComponent(mediaId)}`);
}

export function deleteMediaPoolItem(mediaId) {
  return apiRequest(`/admin/media/${encodeURIComponent(mediaId)}`, {
    method: 'DELETE',
  });
}

/** Deletes the unused files among `ids`; returns { deleted, skipped }. */
export function deleteMediaPoolItems(ids) {
  return apiRequest('/admin/media/delete', {
    method: 'POST',
    body: { ids },
  });
}

// Departments the signed-in user may author in; the create-article form uses
// this to decide between applying one silently and offering a choice.
// Departments the user may create in: articles (articles.manage) or, with
// isNews, news (news.manage).
export function getAuthoringDepartments(isNews = false) {
  return apiRequest(
    `/articles/authoring-departments${isNews ? '?is_news=true' : ''}`,
  );
}

/*
 * Platform settings (branding)
 */

export function getPlatformSettings() {
  return apiRequest('/platform-settings');
}

export function updatePlatformSettings(settings) {
  return apiRequest('/platform-settings', {
    method: 'PATCH',
    body: settings,
  });
}

export function uploadPlatformLogo(file) {
  const formData = new FormData();

  formData.append('file', file);

  return apiRequest('/platform-settings/logo', {
    method: 'POST',
    body: formData,
  });
}

export function removePlatformLogo() {
  return apiRequest('/platform-settings/logo', {
    method: 'DELETE',
  });
}


/*
 * About page
 */

export function getAboutPage() {
  return apiRequest('/about-page');
}

export function updateAboutPage(content) {
  return apiRequest('/about-page', {
    method: 'PATCH',
    body: { content },
  });
}

/*
 * Sidebar items
 */

export function getSidebarItems() {
  return apiRequest(`/sidebar`);
}

export function getSidebarItem(id) {
  return apiRequest(`/sidebar/${encodeURIComponent(id)}`);
}

export function createSidebarItem(item) {
  return apiRequest('/sidebar/', { method: 'POST', body: item });
}

export function updateSidebarItem(id, item) {
  return apiRequest(`/sidebar/${encodeURIComponent(id)}`, { method: 'PATCH', body: item });
}

export function deleteSidebarItem(id) {
  return apiRequest(`/sidebar/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export function getSidebarItemArticles(id) {
  return apiRequest(
    `/sidebar/${encodeURIComponent(id)}/articles`,
  );
}

export function archiveArticle(id) {
  return apiRequest(`/articles/${encodeURIComponent(id)}/archive`, {
    method: 'PATCH',
  });
}

export function getArchivedArticles() {
  return apiRequest('/articles/archive');
}

export function deleteArchivedArticle(id) {
  return apiRequest(`/articles/archive/${id}`, {
    method: 'DELETE',
  });
}

export const getTags = () => apiRequest('/articles/tags');

export const updateTag = (id, data) =>
    apiRequest(`/articles/tags/${id}`, {
        method: 'PATCH',
        body: data,
    });

export const deleteTag = (id) =>
    apiRequest(`/articles/tags/${id}`, {
        method: 'DELETE',
    });

export const replaceTag = (sourceId, targetTagId) =>
    apiRequest(`/articles/tags/${sourceId}/replace`, {
        method: 'POST',
        body: JSON.stringify({ targetTagId }),
    });

export function getArchivedArticleById(id) {
  return apiRequest(
    `/articles/archive/${encodeURIComponent(id)}`
  );
}

export function getLatestArchivedArticles() {
  return apiRequest('/articles/archive/latest');
}

export function restoreArticleVersion(id) {
  return apiRequest(
    `/articles/archive/${encodeURIComponent(id)}/restore`,
    {
      method: 'POST',
    }
  );
}

export function getArticleById(id) {
  return apiRequest(`/articles/${encodeURIComponent(id)}`);
}

export function getArticleHistory(id) {
  return apiRequest(
    `/articles/${encodeURIComponent(id)}/history`
  );
}

/*
 * Plugins (documentation/plugins.md)
 */

/** Plugins switched on anywhere, with the departments they are on in. */
export function getPlugins() {
  return apiRequest('/plugins');
}

/** Installed plugins and where the caller may manage them (plugins.manage). */
export function getAdminPlugins() {
  return apiRequest('/admin/plugins');
}

/** departmentId null: for all departments; enabled null: the department follows the global switch again. */
export function setPluginEnabled(pluginId, departmentId, enabled) {
  return apiRequest(`/admin/plugins/${encodeURIComponent(pluginId)}/enabled`, {
    method: 'PUT',
    body: { departmentId, enabled },
  });
}

/** One level's settings: a department's, or (null) the global ones. */
export function getPluginSettings(pluginId, departmentId) {
  const query = departmentId ? `?department=${encodeURIComponent(departmentId)}` : '';
  return apiRequest(`/admin/plugins/${encodeURIComponent(pluginId)}/settings${query}`);
}

/** Stores values of one level; a value of null clears it, so the next level applies. */
export function setPluginSettings(pluginId, departmentId, values) {
  return apiRequest(`/admin/plugins/${encodeURIComponent(pluginId)}/settings`, {
    method: 'PUT',
    body: { departmentId, values },
  });
}
